/**
 * =============================================================================
 * FORM RESUME — API hydration & URL/session helpers
 * =============================================================================
 *
 * Purpose
 * -------
 * When a patient opens a referral link such as:
 *   /en?ref=PARTNER&formUid=F001360
 *   /en?ref=PARTNER&formid=F001360   (case-insensitive)
 *
 * …this module loads the existing draft from:
 *   GET /api/v2/patient-intake/forms/{formUid}
 *
 * …and maps the server payload back into `IntakeData` for the intake UI.
 *
 * Production safety rules (read before changing)
 * --------------------------------------------
 * 1. API data is the source of truth during resume — never merge browser cache
 *    on top of API fields (see `buildResumeIntakeState`).
 * 2. Resume is skipped when `?token=` is present (trusted magic-link flow owns
 *    bootstrap). Same for `?verified=1&formUid=` (handled in IntakeV2Page).
 * 3. Locked forms show a barrier UI — no editing after submit/verify/finalize.
 *    Locked when status is in LOCKED_FORM_STATUSES (incl. `verified`) or when
 *    the API returns a non-empty `submittedAt` (belt-and-suspenders for new statuses).
 * 4. `formUid` must match `normalizeResumeFormUid` before any network call.
 * 5. Disable globally with `VITE_ENABLE_FORM_RESUME=false` if rollback is needed.
 *
 * Session storage
 * ---------------
 * After a successful hydrate, `formUid` is stored in sessionStorage so a refresh
 * re-fetches the same form even after we strip `formUid` from the URL (keeps `ref`).
 */

import type { IntakeData, UploadedMedia } from "@/lib/types";
import type { ProcedureType } from "@/lib/specialtyTags";
import type { Specialty } from "@/lib/patientIntakeApi";
import { buildLabelToCodeMaps } from "@/lib/intakeOptionMaps";
import {
  parsePrefilledPhone,
  repairIntakePhoneFields,
} from "@/lib/phoneNumber";

// ─── Feature flag & session key ───────────────────────────────────────────────

/** sessionStorage key — survives refresh; cleared on “Start new intake”. */
export const RESUME_FORM_UID_SESSION_KEY = "jema_resume_form_uid";

/**
 * Enabled by default in production.
 * Set `VITE_ENABLE_FORM_RESUME=false` to disable resume without redeploying logic.
 */
export const ENABLE_FORM_RESUME =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_ENABLE_FORM_RESUME !== "false") ||
  typeof import.meta === "undefined";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Backend statuses where the patient must not resume editing this formUid.
 * Keep in sync with Jema API — `verified` is post-submit + OTP (e.g. F001360).
 */
const LOCKED_FORM_STATUSES = new Set([
  "submitted",
  "verified",
  "finalized",
  "finalised",
  "completed",
  "complete",
]);

/** Query param names accepted for form id (case-insensitive key match). */
const FORM_ID_QUERY_KEYS = new Set(["formuid", "formid"]);

/** Expected server form id shape, e.g. F001360. Rejects garbage before fetch. */
const FORM_UID_PATTERN = /^F[A-Z0-9]+$/i;

export type IntakeFormApiResponse = {
  id?: string;
  formUid: string;
  referenceUid?: string;
  patientEmail?: string;
  patientId?: string;
  specialty?: string;
  status?: string;
  /** Set when the patient completes submit — used as a lock signal even if status is new. */
  submittedAt?: string | null;
  verifiedAt?: string | null;
  identity?: Record<string, unknown>;
  responses?: Array<{ questionId: string; answer: unknown; question?: unknown }>;
  media?: Array<Record<string, unknown>>;
  portalContext?: string;
  verificationAttempts?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type FormResumeResult =
  | { kind: "submitted"; form: IntakeFormApiResponse }
  | { kind: "draft"; intake: Partial<IntakeData>; form: IntakeFormApiResponse };

// ─── URL & session helpers ────────────────────────────────────────────────────

function readFormIdFromParams(params: URLSearchParams): string | null {
  for (const [key, value] of params.entries()) {
    if (!FORM_ID_QUERY_KEYS.has(key.toLowerCase())) continue;
    const normalized = normalizeResumeFormUid(value);
    if (normalized) return normalized;
  }
  return null;
}

/**
 * Normalize and validate a form id from URL, session, or external links.
 * Returns null when empty or malformed (prevents accidental API calls).
 */
export function normalizeResumeFormUid(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed || !FORM_UID_PATTERN.test(trimmed)) return null;
  return trimmed.toUpperCase();
}

/**
 * Read resume formUid from query string.
 * Returns null when `token` is present — trusted session must not double-bootstrap.
 */
export function getResumeFormUidFromSearch(
  search?: string | URLSearchParams,
): string | null {
  if (typeof window === "undefined" && search === undefined) return null;

  const params =
    search instanceof URLSearchParams
      ? search
      : new URLSearchParams(
          search ??
            (typeof window !== "undefined" ? window.location.search : ""),
        );

  if (params.get("token")?.trim()) return null;

  return readFormIdFromParams(params);
}

export function getStoredResumeFormUid(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return normalizeResumeFormUid(
      window.sessionStorage.getItem(RESUME_FORM_UID_SESSION_KEY),
    );
  } catch {
    return null;
  }
}

export function setStoredResumeFormUid(formUid: string | null): void {
  if (typeof window === "undefined") return;
  const normalized = normalizeResumeFormUid(formUid);
  try {
    if (normalized) {
      window.sessionStorage.setItem(RESUME_FORM_UID_SESSION_KEY, normalized);
    } else {
      window.sessionStorage.removeItem(RESUME_FORM_UID_SESSION_KEY);
    }
  } catch {
    // Private mode / blocked storage — resume still works for this tab via state.
  }
}

/** Remove form id from URL but keep `ref` and other params. */
export function clearResumeFormUidFromUrl(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  let hadFormUid = false;
  for (const key of [...params.keys()]) {
    if (!FORM_ID_QUERY_KEYS.has(key.toLowerCase())) continue;
    hadFormUid = true;
    params.delete(key);
  }
  if (!hadFormUid) return;

  const query = params.toString();
  const newUrl = query
    ? `${window.location.pathname}?${query}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`;
  const prevState = window.history.state;
  window.history.replaceState(
    prevState && typeof prevState === "object" && !Array.isArray(prevState)
      ? { ...(prevState as Record<string, unknown>) }
      : {},
    "",
    newUrl,
  );
}

/** Remove form id keys from a URLSearchParams instance (for React Router updates). */
export function stripFormIdSearchParams(params: URLSearchParams): void {
  for (const key of [...params.keys()]) {
    if (FORM_ID_QUERY_KEYS.has(key.toLowerCase())) {
      params.delete(key);
    }
  }
}

/** Extra query keys stripped on “Start a new intake” — clean URL like a normal fresh open. */
const FRESH_INTAKE_STRIP_KEYS = [
  "ref",
  "verified",
  "token",
  "referenceUid",
  "source",
] as const;

/** Remove resume/link params so the patient starts with a plain `/en` or `/es` URL. */
export function stripFreshIntakeSearchParams(params: URLSearchParams): void {
  stripFormIdSearchParams(params);
  for (const key of FRESH_INTAKE_STRIP_KEYS) {
    params.delete(key);
  }
}

/** True when the API `status` alone means the form is no longer an editable draft. */
export function isSubmittedFormStatus(status?: string | null): boolean {
  if (!status) return false;
  return LOCKED_FORM_STATUSES.has(status.trim().toLowerCase());
}

function hasSubmittedTimestamp(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * Whether resume must show the submitted barrier instead of hydrating the wizard.
 * Prefer this over `isSubmittedFormStatus` alone — also respects `submittedAt`.
 */
export function isFormLockedForResume(
  form: Pick<IntakeFormApiResponse, "status" | "submittedAt">,
): boolean {
  if (isSubmittedFormStatus(form.status)) return true;
  return hasSubmittedTimestamp(form.submittedAt);
}

/** Infer locale from `/en` vs `/es` route when API has no preferredLanguage. */
export function getPathLocale(): "en" | "es" {
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/es")) {
    return "es";
  }
  return "en";
}

// ─── API → IntakeData mapping ─────────────────────────────────────────────────

function asString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeSlug(value: string): string {
  return value.trim().toLowerCase().replace(/_/g, "-");
}

/** Map API `specialty` string to a ProcedureType object when the list is loaded. */
export function resolveProcedureFromSpecialty(
  specialty: string | undefined,
  specialties: Specialty[] | undefined,
): ProcedureType | string {
  if (!specialty?.trim()) return "";

  const normalized = normalizeSlug(specialty);
  const match = specialties?.find(
    (s) =>
      normalizeSlug(s.slug) === normalized ||
      normalizeSlug(s.category) === normalized ||
      (s.enum_slug && normalizeSlug(s.enum_slug) === normalized),
  );

  if (match) {
    return {
      id: match.id,
      slug: match.slug,
      name: match.name,
      category: match.category,
      description: match.description,
      display_order: match.display_order,
      enum_slug: match.enum_slug,
    } satisfies ProcedureType;
  }

  return normalized;
}

function reverseMapAnswer(
  questionId: string,
  answer: unknown,
  labelToCode: Record<string, Record<string, string>>,
): unknown {
  if (answer === null || answer === undefined) return answer;

  const mapping = labelToCode[questionId];
  if (!mapping) return answer;

  if (Array.isArray(answer)) {
    return answer.map((item) => {
      if (typeof item !== "string") return item;
      return mapping[item] ?? mapping[item.toLowerCase()] ?? item;
    });
  }

  if (typeof answer === "string") {
    return mapping[answer] ?? mapping[answer.toLowerCase()] ?? answer;
  }

  return answer;
}

function inferUploadCategory(
  photoPosition: string | undefined,
  mimeType: string,
  filename: string,
): "idPassport" | "labsImaging" | "photos" {
  const position = (photoPosition ?? "").replace(/-/g, "_").toLowerCase();
  if (position === "idpassport" || position === "id_passport") {
    return "idPassport";
  }
  if (position === "labsimaging" || position === "labs_imaging") {
    return "labsImaging";
  }
  if (position === "photos") {
    return "photos";
  }

  const type = mimeType.toLowerCase();
  const name = filename.toLowerCase();
  if (
    type === "application/pdf" ||
    type === "application/dicom" ||
    type === "image/tiff" ||
    name.endsWith(".pdf") ||
    name.endsWith(".dcm") ||
    /\.tiff?$/.test(name)
  ) {
    return "labsImaging";
  }
  if (type.startsWith("image/")) {
    return "photos";
  }
  return "labsImaging";
}

function normalizeApiMediaRecord(
  item: Record<string, unknown>,
): UploadedMedia | null {
  const id = asString(item.id) ?? asString(item.mediaId);
  if (!id) return null;

  const filename =
    asString(item.originalFilename) ??
    asString(item.filename) ??
    "upload";
  const mimeType = asString(item.mimeType) ?? "application/octet-stream";

  return {
    id,
    filename,
    mimeType,
    size:
      typeof item.fileSize === "number"
        ? item.fileSize
        : Number(item.size ?? 0),
    downloadUrl: asString(item.downloadUrl) ?? "",
    mediaType: asString(item.mediaType) ?? "photo",
    status: asString(item.status) ?? "completed",
    isUploaded: true,
  };
}

function mapMediaToUploads(
  media: Array<Record<string, unknown>> | undefined,
): IntakeData["uploads"] {
  const uploads: IntakeData["uploads"] = {
    idPassport: [],
    labsImaging: [],
    photos: [],
  };

  if (!Array.isArray(media)) return uploads;

  for (const item of media) {
    const uploaded = normalizeApiMediaRecord(item);
    if (!uploaded) continue;

    const category = inferUploadCategory(
      asString(item.photoPosition),
      uploaded.mimeType,
      uploaded.filename,
    );
    uploads[category]!.push(uploaded);
  }

  return uploads;
}

/**
 * GET /forms/{formUid} embeds a reduced media shape (mediaId, no downloadUrl).
 * GET /forms/{formUid}/media returns full records — prefer these for resume hydrate.
 */
export function mergeResumeFormMedia(
  form: IntakeFormApiResponse,
  fullMedia?: Array<Record<string, unknown>>,
): IntakeFormApiResponse {
  if (Array.isArray(fullMedia) && fullMedia.length > 0) {
    return { ...form, media: fullMedia };
  }
  return form;
}

/**
 * Map GET /patient-intake/forms/{formUid} into partial IntakeData.
 * Does not merge browser cache — caller uses `buildResumeIntakeState` for that.
 */
export function mapApiFormToIntakeData(
  form: IntakeFormApiResponse,
  options?: {
    specialties?: Specialty[];
    /** URL prefill fields (referral link) — applied only where API omitted a value. */
    urlPrefill?: Partial<IntakeData>;
  },
): Partial<IntakeData> {
  if (!normalizeResumeFormUid(form.formUid)) {
    throw new Error("Invalid form response: missing formUid");
  }

  const labelToCode = buildLabelToCodeMaps();
  const identity = form.identity ?? {};
  const patch: Partial<IntakeData> = {
    formUid: normalizeResumeFormUid(form.formUid) ?? undefined,
    referenceUid: form.referenceUid,
    specialty: form.specialty,
  };

  const firstName = asString(identity.firstName);
  const lastName = asString(identity.lastName);
  const email = asString(identity.email)?.toLowerCase();
  const phoneRaw = asString(identity.phone);
  const country = asString(identity.country);
  const phoneCountry =
    asString(identity.phoneCountry) ??
    country ??
    options?.urlPrefill?.country ??
    "US";

  if (firstName) patch.firstName = firstName;
  if (lastName) patch.lastName = lastName;
  if (email) patch.email = email;
  if (country) patch.country = country;
  patch.phoneCountry = phoneCountry;

  if (phoneRaw) {
    const parsed = parsePrefilledPhone(phoneRaw, phoneCountry);
    if (parsed) {
      patch.country = parsed.country;
      patch.phoneCountry = parsed.country;
      patch.phone = parsed.national;
    } else {
      const repaired = repairIntakePhoneFields(phoneRaw, phoneCountry);
      patch.phone = repaired.phone;
      patch.country = repaired.country || phoneCountry;
    }
  }

  const preferredLanguage = asString(identity.preferredLanguage);
  if (preferredLanguage === "en" || preferredLanguage === "es") {
    patch.preferredLanguage = preferredLanguage;
  }

  const identityKeys = [
    "dateOfBirth",
    "city",
    "state",
    "height",
    "weight",
  ] as const;
  for (const key of identityKeys) {
    const value = asString(identity[key]);
    if (value) (patch as Record<string, unknown>)[key] = value;
  }

  patch.procedures = resolveProcedureFromSpecialty(
    form.specialty,
    options?.specialties,
  );

  const documentLinks: IntakeData["documentReferenceLinks"] = [];

  for (const response of form.responses ?? []) {
    const questionId = response.questionId;
    if (!questionId) continue;

    if (/^documentReferenceLinks_\d+$/.test(questionId)) {
      const url = asString(response.answer);
      if (url) documentLinks.push({ url });
      continue;
    }

    if (questionId in patch) continue;

    const normalized = reverseMapAnswer(
      questionId,
      response.answer,
      labelToCode,
    );
    (patch as Record<string, unknown>)[questionId] = normalized;
  }

  if (documentLinks.length > 0) {
    patch.documentReferenceLinks = documentLinks;
  }

  patch.uploads = mapMediaToUploads(form.media);

  return patch;
}

/**
 * Build full intake state for resume.
 *
 * Merge order (later wins):
 *   base defaults → URL prefill (referral) → API payload
 *
 * Intentionally does NOT use localStorage cache — that caused “only name filled”
 * bugs when resume never ran but stale cache partially populated the UI.
 */
export function buildResumeIntakeState(
  apiPatch: Partial<IntakeData>,
  options: {
    base: IntakeData;
    urlPrefill?: Partial<IntakeData>;
    pathLocale?: "en" | "es";
  },
): IntakeData {
  const pathLocale = options.pathLocale ?? getPathLocale();
  const merged: IntakeData = {
    ...options.base,
    ...(options.urlPrefill ?? {}),
    ...apiPatch,
    consentGiven: false,
    emailVerified: false,
    preferredLanguage:
      apiPatch.preferredLanguage === "en" || apiPatch.preferredLanguage === "es"
        ? apiPatch.preferredLanguage
        : options.urlPrefill?.preferredLanguage === "en" ||
            options.urlPrefill?.preferredLanguage === "es"
          ? options.urlPrefill.preferredLanguage
          : pathLocale,
    uploads: apiPatch.uploads ?? options.base.uploads,
    documentReferenceLinks:
      apiPatch.documentReferenceLinks ?? options.base.documentReferenceLinks,
  };

  const repairedPhone = repairIntakePhoneFields(merged.phone, merged.country);
  return {
    ...merged,
    phone: repairedPhone.phone,
    country: repairedPhone.country,
    phoneCountry: merged.phoneCountry ?? repairedPhone.country,
  };
}

export function classifyFormResume(
  form: IntakeFormApiResponse,
  options?: {
    specialties?: Specialty[];
    urlPrefill?: Partial<IntakeData>;
  },
): FormResumeResult {
  if (isFormLockedForResume(form)) {
    return { kind: "submitted", form };
  }

  return {
    kind: "draft",
    form,
    intake: mapApiFormToIntakeData(form, options),
  };
}

/** After specialties load, upgrade string slug → full ProcedureType for UI cards. */
export function upgradeProcedureIfNeeded(
  current: IntakeData["procedures"],
  specialties: Specialty[],
): IntakeData["procedures"] | null {
  if (typeof current === "object" && current !== null && "slug" in current) {
    return null;
  }
  const slug = typeof current === "string" ? current : "";
  if (!slug.trim() || specialties.length === 0) return null;
  const upgraded = resolveProcedureFromSpecialty(slug, specialties);
  if (typeof upgraded === "string" && upgraded === slug) return null;
  return upgraded;
}
